import { useCallback, useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
    Service,
    UpdatePlan,
    Plan,
    CreateService,
    UpdateService,
    CreatePlan,
} from '@app-types/service.type';
import Header from '@components/header/header.component';
import View from '@components/view/view';
import ModuleSummary from '@components/module-summary/module-summary.component';
import { usePermissions } from '@hooks/usePermissions';
import { ApiPermissions } from '@constants/api-permissions';
import { subscriptionsSDK } from '@services/subscriptions';
import { useServicesStore } from '@stores/services-store';
import { useAuthStore } from '@stores/auth-store';
import StringUtil from '@utils/string.util';

import { useThemeStore } from '../../../store';

import { ServiceDialog } from './containers/service-dialog/service-dialog';
import { ServiceActionList } from './containers/service-action-list';
import { ServiceList } from './containers/service-list/service-list';
import { DeletePlanModal } from './containers/delete-plan-modal/delete-plan-modal';
import { PlanDialog } from './containers/plan-dialog/plan-dialog';
import { serviceSummaryMapper } from './config';

const ViewAllServicesView = () => {
    const { theme } = useThemeStore();
    const { permissions } = useAuthStore();
    const { hasPermission } = usePermissions();
    const { fetchFrequencies } = useServicesStore();

    const [totalSummary, setTotalSummary] = useState([
        { label: 'Total servicios', value: '-' },
    ]);
    // Service states
    const [selectedService, setSelectedService] = useState<Service | undefined>(
        undefined
    );
    const [filteredServices, setFilteredServices] = useState<Service[]>([]);
    const [isUpdatingService, setIsUpdatingService] = useState(false);
    const [showServiceDialog, setShowServiceDialog] = useState(false);
    // Plan states
    const [shownDeletePlan, setShowDeletePlan] = useState(false);
    const [isDeletingPlan, setIsDeletingPlan] = useState(false);
    const [planToDelete, setPlanToDelete] = useState<Plan | undefined>(
        undefined
    );
    const [planToEdit, setPlanToEdit] = useState<Plan | undefined>(undefined);
    const [showEditPlan, setShowEditPlan] = useState(false);
    const [isUpdatingPlan, setIsUpdatingPlan] = useState(false);
    // This state is for plan creation
    const [serviceId, setServiceId] = useState<number | undefined>(undefined);

    const queryClient = useQueryClient();

    const updatePlanMutation = useMutation({
        mutationFn: ({ id, data }: { id: number; data: UpdatePlan }) => {
            setIsUpdatingPlan(true);
            return subscriptionsSDK.admin.plans.edit(id, data);
        },
        onSuccess: ({ updated }: { updated: boolean }) => {
            setIsUpdatingPlan(false);
            if (!updated) return;
            setShowEditPlan(false);
            queryClient.invalidateQueries({ queryKey: ['services'] });
        },
        onError: () => setIsUpdatingPlan(false),
    });

    const deletePlanMutation = useMutation({
        mutationFn: ({ id }: { id: number }) => {
            setIsDeletingPlan(true);
            return subscriptionsSDK.admin.plans.delete(id);
        },
        onSuccess: ({ deleted }: { deleted: boolean }) => {
            setIsDeletingPlan(false);
            if (!deleted) return;
            setShowDeletePlan(false);
            queryClient.invalidateQueries({ queryKey: ['services'] });
        },
        onError: () => setIsUpdatingPlan(false),
    });

    const { data: services } = useQuery({
        queryKey: ['services'],
        queryFn: async (): Promise<Service[]> => {
            return subscriptionsSDK.admin.services.getAll();
        },
        enabled: hasPermission(ApiPermissions.READ_SERVICES),
    });

    useEffect(() => {
        fetchFrequencies();
    }, []);

    useEffect(() => {
        if (!services) return;

        setFilteredServices(services);

        const totalsData: Record<string, any> = {
            total: services.length,
        };
        setTotalSummary(
            Object.keys(totalsData).map((key) => ({
                label: serviceSummaryMapper[key],
                value: totalsData[key],
            }))
        );
    }, [services]);

    const findPlanById = useCallback(
        (id: number) => {
            if (!services) return;

            for (const service of services) {
                const plan = service.plans?.find((plan) => plan.id === id);
                if (plan) return plan;
            }

            return;
        },
        [services]
    );

    const requestPlanCreation = (serviceId: number) => {
        if (!hasPermission(ApiPermissions.CREATE_PLANS)) return;
        setServiceId(serviceId);
        setShowEditPlan(true);
    };

    const requestPlanEdition: (id: number) => void = useCallback(
        (id) => {
            if (!hasPermission(ApiPermissions.UPDATE_PLANS)) return;
            const plan = findPlanById(id);
            if (!plan || plan.deletedAt) return;
            setPlanToEdit(plan);
            setShowEditPlan(true);
        },
        [findPlanById]
    );

    const requestServiceEdition = (id: number) => {
        if (!hasPermission(ApiPermissions.UPDATE_SERVICES)) return;
        const service = services?.find((service) => service.id === id);
        if (!service) return;
        setSelectedService(service);
        setShowServiceDialog(true);
    };

    const discardServiceDialog = () => {
        setSelectedService(undefined);
        setShowServiceDialog(false);
    };

    const discardPlanDialog = useCallback(() => {
        setPlanToEdit(undefined);
        setShowEditPlan(false);
    }, []);

    const requestPlanDeletion: (id: number) => void = useCallback(
        (id) => {
            if (!hasPermission(ApiPermissions.DELETE_PLANS)) return;
            const plan = findPlanById(id);
            if (!plan) return;
            setPlanToDelete(plan);
            setShowDeletePlan(true);
        },
        [findPlanById]
    );

    const discardPlanDeletion = useCallback(() => {
        setPlanToDelete(undefined);
        setShowDeletePlan(false);
    }, []);

    const confirmPlanDeletion = useCallback(() => {
        if (!planToDelete) return;
        deletePlanMutation.mutate({ id: planToDelete.id });
    }, [planToDelete]);

    const handlePlanCreation = useCallback(
        async (payload: CreatePlan) => {
            if (!serviceId) return;
            setIsUpdatingPlan(true);
            const service = await subscriptionsSDK.admin.plans.create({
                ...payload,
                serviceId,
            });
            setIsUpdatingPlan(false);
            if (!service) return;
            queryClient.invalidateQueries({ queryKey: ['services'] });
            setSelectedService(undefined);
            setShowEditPlan(false);
        },
        [serviceId]
    );

    const requestServiceCreation = () => {
        if (!hasPermission(ApiPermissions.CREATE_SERVICES)) return;
        setSelectedService(undefined);
        setShowServiceDialog(true);
    };

    const handleServiceCreation = async (data: CreateService) => {
        const result = await subscriptionsSDK.admin.services.create(data);
        if (!result) return false;
        queryClient.invalidateQueries({ queryKey: ['services'] });
        setShowServiceDialog(false);
        return true;
    };

    const handleServiceUpdate = async (data: UpdateService) => {
        if (!selectedService?.id) return false;
        setIsUpdatingService(true);
        const { updated } = await subscriptionsSDK.admin.services.update(
            selectedService.id,
            data
        );
        setIsUpdatingService(false);
        if (!updated) return false;
        queryClient.invalidateQueries({ queryKey: ['services'] });
        setSelectedService(undefined);
        setShowServiceDialog(false);
        return true;
    };

    const filterServices = (query: string) => {
        if (!query.trim()) return setFilteredServices(services ?? []);
        const filtered = services?.filter((s) => {
            const name = StringUtil.normalize(s.name);
            return name.includes(StringUtil.normalize(query));
        });
        setFilteredServices(filtered ?? []);
    };

    return (
        <View permissions={permissions} requiredPermission="read-services">
            <Header
                title="Servicios"
                subtitle="Aquí puedes ver todos los servicios"
            />
            <ModuleSummary mode={theme} summary={totalSummary} />
            <ServiceActionList
                onCreateService={requestServiceCreation}
                onFilter={filterServices}
            />
            <ServiceList
                services={filteredServices}
                onCreatePlan={requestPlanCreation}
                onEditPlan={requestPlanEdition}
                onEditService={requestServiceEdition}
                onDeletePlan={requestPlanDeletion}
            />
            {shownDeletePlan && (
                <DeletePlanModal
                    loading={isDeletingPlan}
                    onDiscard={discardPlanDeletion}
                    onConfirm={confirmPlanDeletion}
                />
            )}
            <PlanDialog
                shown={showEditPlan}
                plan={planToEdit}
                updating={isUpdatingPlan}
                onUpdate={updatePlanMutation.mutate}
                onCreate={handlePlanCreation}
                onClose={discardPlanDialog}
            />
            <ServiceDialog
                shown={showServiceDialog}
                service={selectedService}
                onClose={discardServiceDialog}
                onCreate={handleServiceCreation}
                onUpdate={handleServiceUpdate}
                loading={isUpdatingService}
            />
        </View>
    );
};

export default ViewAllServicesView;
